import React from 'react';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';

import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import HeaderItem from '@totem/components/ui_components/headers/HeaderItem';
import NaOptimizePanel from '@totem/components/unauthenticated/ticketing/details/optimize/naOptimizePanel';
import { formatDate, isNotNull } from '@totem/utilities/common';

import '../../../ticketing/ticketing.css';

const styles = {
  description: {
    whiteSpace: 'pre-wrap' as 'pre-wrap',
  },
};

type Props = {
  ticket: TicketQueryResult;
};

const NATicketHeader = ({ ticket }: Props) => {
  const getBuildingDisplay = (ticketData: TicketQueryResult) => {
    if (ticketData.building !== null) {
      return <span>{ticketData.building.name}</span>;
    }
    return <span>-</span>;
  };

  const getControlSystemAlternate = (ticketData: TicketQueryResult) => {
    if (
      typeof ticketData !== 'undefined' &&
      ticketData !== null &&
      ticketData.ticket.attributes !== null
    ) {
      for (let idx = 0; idx < ticketData.ticket.attributes.length; idx++) {
        const attribute = ticketData.ticket.attributes[idx];
        if (attribute.name === 'ControlSystemType') {
          return attribute.value;
        }
      }
    }
    return '';
  };

  const getControlSystemDisplay = (ticketData: TicketQueryResult) => {
    if (
      ticketData.controlSystem !== null &&
      ticketData.controlSystem.name !== ''
    ) {
      return <span>{ticketData.controlSystem.name}</span>;
    }

    return <span>{getControlSystemAlternate(ticketData)}</span>;
  };

  const getDeviceDisplay = (ticketData: TicketQueryResult) => {
    if (ticketData.device !== null) {
      return <span>{ticketData.device.name}</span>;
    }
    return <span>-</span>;
  };

  return (
    <div>
      <Row>
        <Col span={6}>
          <HeaderItem title="Summary">{ticket.ticket.summary}</HeaderItem>
          <HeaderItem title="Status">
            {ticket.ticket.status}
            {ticket.ticket.subStatus !== '' && ` / ${ticket.ticket.subStatus}`}
          </HeaderItem>
          <HeaderItem title="Priority">{ticket.ticket.priority}</HeaderItem>
        </Col>
        <Col span={6}>
          {typeof ticket.ticket.category !== 'undefined' &&
            ticket.ticket.category !== null &&
            ticket.ticket.category !== '' && (
              <HeaderItem title="Category">{ticket.ticket.category}</HeaderItem>
            )}
          {typeof ticket.ticket.subCategory !== 'undefined' &&
            ticket.ticket.subCategory !== null &&
            ticket.ticket.subCategory !== '' && (
              <HeaderItem title="SubCategory">
                {ticket.ticket.subCategory}
              </HeaderItem>
            )}
        </Col>
        <Col span={6}>
          <HeaderItem title="Organization">
            {ticket.organization.name}
          </HeaderItem>
          {ticket.region.name !== '' && (
            <HeaderItem title="Region">{ticket.region.name}</HeaderItem>
          )}
          {ticket.building.name !== '' && (
            <HeaderItem title="Building">
              {getBuildingDisplay(ticket)}
            </HeaderItem>
          )}
          {(ticket.controlSystem.name !== '' ||
            getControlSystemAlternate(ticket) !== '') && (
            <HeaderItem title="Control System">
              {getControlSystemDisplay(ticket)}
            </HeaderItem>
          )}
          {ticket.device.name !== '' && (
            <HeaderItem title="Device">{getDeviceDisplay(ticket)}</HeaderItem>
          )}
        </Col>
        <Col span={6}>
          <HeaderItem title="Created">
            {formatDate(ticket.ticket.createdAt)}
          </HeaderItem>
          <HeaderItem title="Last Updated">
            {formatDate(ticket.ticket.updatedAt)}
          </HeaderItem>
          {isNotNull(ticket.ticket.followUpAt) &&
            ticket.ticket.followUpAt !== '' && (
              <HeaderItem title="Follow Up">
                {dayjs(ticket.ticket.followUpAt).isAfter(dayjs()) ? (
                  <span>{formatDate(ticket.ticket.followUpAt)}</span>
                ) : (
                  <span style={{ color: 'red' }}>
                    {formatDate(ticket.ticket.followUpAt)}
                  </span>
                )}
              </HeaderItem>
            )}
          <HeaderItem title="Closed">
            {formatDate(ticket.ticket.closedAt)}
          </HeaderItem>
        </Col>
      </Row>
      <HeaderItem title="Description">
        <pre style={styles.description}>{ticket.ticket.description}</pre>
      </HeaderItem>
      <br />
      {isNotNull(ticket.ticket.extras) &&
        isNotNull(ticket.ticket.extras.optimize) && <NaOptimizePanel />}
    </div>
  );
};

export default NATicketHeader;
